import { ButtonLinkLeft } from '@zoopla-eng/zds-components/button';
import { Featured } from '@zoopla-eng/zds-components/badges';
import { CallMedium, EmailMedium } from '@zoopla-eng/zds-components/icons';
import { Link } from '@zoopla-eng/zds-components/link';
import { AgentStats } from './AgentStats';
import {
    AgentData,
    EventNamesList,
    Taxonomy,
    trackSegmentAndGAEvent
} from '@zoopla/tracking';
import { Agent } from '../graphql.generated';
import {
    addressStyle,
    callButtonsAreaStyle,
    featuredWrapperStyle,
    imgStyle,
    infoAndStatsAreaStyle,
    infoAreaStyle,
    wrapperStyle
} from './AgentCard.styles.css';
import { fontGroups } from '@zoopla-eng/zds-components/themes';
import { innerCardStyle } from './AgentCard.styles.css';
import { PropsWithChildren } from 'react';

export const AGENT_PROFILE_LINK_TEXT = 'View agent profile';
export const AGENT_PROFILE_LINK_LABEL = (name: string) => `${name} profile`;
export const AGENT_BRANCH_BASE_URL = '/find-agents/branch/';
export const CONTACT_AGENT_BASE_URL = '/find-agents/contact/';
export const CALL_BTN_LABEL = (name: string) => `Call ${name}`;
export const CALL_BTN_TEXT = 'Call';
export const EMAIL_BTN_LABEL = (name: string) => `Email ${name}`;
export const EMAIL_BTN_TEXT = 'Email';

export function AgentCard({
    agentBranch,
    taxonomy,
    agentEcommerceData
}: {
    agentBranch: Agent;
    taxonomy: Taxonomy;
    agentEcommerceData?: AgentData;
}) {
    const trackingHandler = (
        eventName: EventNamesList,
        eventAction: string
    ) => {
        let ecommerce;

        if (
            eventAction === 'view_branch_page_via_title' &&
            agentEcommerceData !== undefined
        ) {
            ecommerce = {
                click: {
                    actionField: {
                        list: 'Find Agents'
                    },
                    products: [agentEcommerceData]
                }
            };
        }

        trackSegmentAndGAEvent(
            eventName,
            {
                eventCategory: 'find_agents',
                eventAction,
                eventLabel: agentBranch.displayName,
                eventValue: agentBranch.id.toString()
            },
            taxonomy.page,
            { ...taxonomy, branch_id: agentBranch.id },
            ecommerce
        );
    };
    return (
        <Card
            key={agentBranch.id}
            featured={agentBranch.featured}
            aria-labelledby={agentBranch.uriName + '__title'}
        >
            <div className={infoAndStatsAreaStyle}>
                <div className={infoAreaStyle}>
                    {agentBranch.logo && (
                        <img
                            className={imgStyle}
                            alt={agentBranch.displayName}
                            src={agentBranch.logo.uri}
                            width={agentBranch.logo.width}
                            height={agentBranch.logo.height}
                        />
                    )}
                    <h2
                        className={fontGroups.mug}
                        id={agentBranch.uriName + '__title'}
                    >
                        {agentBranch.displayName}
                    </h2>
                    <div className={addressStyle}>
                        {agentBranch.displayAddress}
                    </div>
                    <Link
                        href={AGENT_BRANCH_BASE_URL + agentBranch.uriName}
                        onClick={() => {
                            trackingHandler(
                                'Search Result Click',
                                'view_branch_page_via_title'
                            );
                        }}
                        aria-label={AGENT_PROFILE_LINK_LABEL(
                            agentBranch.displayName
                        )}
                    >
                        {AGENT_PROFILE_LINK_TEXT}
                    </Link>
                </div>
                <AgentStats
                    listingsStatistics={agentBranch.listingsStatistics}
                />
            </div>
            <div className={callButtonsAreaStyle}>
                <ButtonLinkLeft
                    href={`tel:${agentBranch.contactNumber}`}
                    rel="nofollow"
                    aria-label={CALL_BTN_LABEL(agentBranch.displayName)}
                    category="tertiary"
                    Icon={CallMedium}
                    onClick={() =>
                        trackingHandler(
                            'Phone Click To Reveal',
                            'view_agent_tel_number'
                        )
                    }
                >
                    {CALL_BTN_TEXT}
                </ButtonLinkLeft>
                <ButtonLinkLeft
                    href={CONTACT_AGENT_BASE_URL + agentBranch.uriName}
                    rel="nofollow"
                    aria-label={EMAIL_BTN_LABEL(agentBranch.displayName)}
                    category="tertiary"
                    Icon={EmailMedium}
                    onClick={() =>
                        trackingHandler('Click Email Agent', 'email_agent')
                    }
                >
                    {EMAIL_BTN_TEXT}
                </ButtonLinkLeft>
            </div>
        </Card>
    );
}

function Card({
    featured,
    children,
    ...props
}: PropsWithChildren<{ featured: boolean }>) {
    return featured ? (
        <li className={featuredWrapperStyle} {...props}>
            <Featured />
            <div className={innerCardStyle({ featured: true })}>{children}</div>
        </li>
    ) : (
        <li className={wrapperStyle} {...props}>
            <div className={innerCardStyle()}>{children}</div>
        </li>
    );
}
